import { useCallback, useEffect, useMemo, useRef } from "react";
import { startFlow, completeFlow, abandonFlow } from "../services/flowTelemetry";

/**
 * Measure one flow, from the screen that owns it (#379).
 *
 * ── WHY A HOOK AND NOT THREE CALLS PER PAGE ──
 *
 * A funnel needs all three events or it reports a confident wrong
 * answer. Started and Completed are easy to remember because they sit
 * where the work is; ABANDONED is the one that gets forgotten, because
 * the whole point of it is that nobody did anything. Left to each page,
 * what ships is Started and Completed everywhere and Abandoned nowhere
 * -- which produces a 0% abandonment rate, and #379's warning applies
 * exactly: "a column that is always zero is worse than a column that is
 * absent, because it reads as a finding".
 *
 * So abandonment is the DEFAULT here. Unmounting without having called
 * the returned function is an abandonment, which is what navigating
 * away from a half-filled form actually is. A page cannot forget to
 * report it, because not reporting is the thing that reports it.
 *
 * ── WHAT IT DOES NOT COVER ──
 *
 * Closing the tab. React runs no cleanup for that, so flowTelemetry's
 * pagehide hook abandons whatever is still open -- the two halves
 * together are why `open` is a module-level map rather than per-hook
 * state.
 *
 * ── TWO EXITS, BOTH OWNED HERE ──
 *
 * `abandoned()` exists for a deliberate exit the app OFFERED -- the Skip
 * on onboarding is the one that prompted it. Leaving that to the unmount
 * path would work, but it would record the abandonment at teardown
 * rather than at the moment the person chose it, and the duration is the
 * figure that suffers.
 *
 * Both mark the flow as ended, so the cleanup cannot fire a SECOND
 * event. Calling flowTelemetry's abandonFlow directly from a page would:
 * the hook would still see an unfinished flow and abandon it again,
 * which is two Abandoneds for one Started -- an abandonment rate above
 * 100% and a funnel nobody can read.
 *
 * ── WHEN THE FLOW IS A MODAL AND NOT A PAGE ──
 *
 * `active` says when the flow is under way, and defaults to true for a
 * page whose mount IS the start. A modal passes its open state: the flow
 * then starts when the dialog opens and is abandoned when it closes
 * unfinished, which is what closing a half-filled dialog is.
 *
 * Without it every modal flow would have to be its own page or report
 * one Started per visit to the screen -- and a screen somebody browses
 * without ever opening the dialog would read as a flow they abandoned,
 * which is the cheapest way to make an abandonment rate meaningless.
 *
 * Usage:
 *   const flow = useFlow(FLOWS.HIVE_ADD);              // a page
 *   const flow = useFlow(FLOWS.EXPENDITURE, open);     // a dialog
 *   ... on success: flow.finished();
 *   ... on a deliberate exit: flow.abandoned();
 */
export function useFlow(name, active = true) {
  /* A REF, not state. Completing must not re-render the page -- these
     events are a by-product of work the user already did, and a render
     caused by the measurement is the measurement changing the thing
     measured. */
  const completed = useRef(false);

  useEffect(() => {
    if (!active) return undefined;
    completed.current = false;
    startFlow(name);
    return () => {
      if (!completed.current) abandonFlow(name);
    };
  }, [name, active]);

  /* GUARDED, so a double submit is one completion. Two Completeds for
     one Started would push the completion rate above 100%, which makes
     the whole figure unreadable rather than slightly wrong. */
  const finished = useCallback(() => {
    if (completed.current) return;
    completed.current = true;
    completeFlow(name);
  }, [name]);

  const abandoned = useCallback(() => {
    if (completed.current) return;
    // The same flag for both: what it really means is "this flow has
    // ended", and the cleanup must not add an event either way.
    completed.current = true;
    abandonFlow(name);
  }, [name]);

  /* A MEMOISED OBJECT, so `flow` is stable and can sit in another
     effect's dependency list without re-running it -- which on a flow
     hook would mean restarting the flow it is measuring. */
  return useMemo(() => ({ finished, abandoned }), [finished, abandoned]);
}
